import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../../../shared/components';
import { useMe } from '../../../shared/hooks/useMe';
import {
  canReadQuestionnaire,
  canTrainHelios,
  canWriteQuestionnaireFlow,
} from '../../../shared/permissions/capabilities';
import {
  colors,
  typography,
  cardStyles,
  primaryButtonStyles,
  secondaryButtonStyles,
} from '../../../shared/styles/theme';
import UpdateKnowledgeBaseButton from './UpdateKnowledgeBaseButton';

const QuestionnaireHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { me } = useMe();
  const canWriteQuest = canWriteQuestionnaireFlow(me);
  const canReadQuest = canReadQuestionnaire(me);
  const showTrainHelios = canTrainHelios(me);

  const buttonBaseStyles = {
    minHeight: '44px',
    minWidth: '180px',
    px: 3,
    fontSize: typography.fontSize.md,
  };

  return (
    <PageLayout>
      <Box sx={{ textAlign: 'center', mb: 0 }}>
        <Typography
          variant="h2"
          sx={{
            color: colors.text.primary,
            fontSize: typography.fontSize['3xl'],
            fontWeight: typography.fontWeight.bold,
            mb: 2,
          }}
        >
          Your AI-powered knowledge assistant
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: colors.text.secondary,
            fontSize: typography.fontSize.lg,
            maxWidth: '900px',
            mx: 'auto',
            lineHeight: typography.lineHeight.relaxed,
          }}
        >
          OneSec combines the power of AI with Thoughtworks knowledge and data to provide accurate answers and insights. You can submit and retrieve client security questionnaires, or train OneSec to improve the quality and accuracy of security data.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '30px',
          mt: '30px',
          maxWidth: '960px',
          mx: 'auto',
        }}
      >
        {/* First Card: Client security questions answered */}
        <Card
          elevation={2}
          sx={{
            ...cardStyles,
            display: 'flex',
            flexDirection: 'column',
            px: '48px',
            pt: '30px',
            pb: '30px',
          }}
        >
          <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: '24px',
                mx: 'auto',
              }}
            >
              <img
                src="/Helios_securityQuestions.ico"
                alt="Security Questions Icon"
                style={{ width: 60, height: 60, objectFit: 'contain' }}
              />
            </Box>

            <Typography
              variant="h3"
              sx={{
                fontWeight: typography.fontWeight.bold,
                fontSize: typography.fontSize.xl,
                color: colors.text.primary,
                mb: '24px',
                textAlign: 'center',
              }}
            >
              Client security questions answered
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: colors.text.secondary,
                fontSize: typography.fontSize.md,
                mb: '24px',
                textAlign: 'center',
              }}
            >
              View existing questionnaires or upload a new client security question for answers from our knowledge base.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '12px',
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              {canWriteQuest && (
                <Button
                  variant="outlined"
                  onClick={() => navigate('/questionnaire/questions')}
                  sx={{ ...buttonBaseStyles, ...secondaryButtonStyles }}
                >
                  Enter client questions
                </Button>
              )}
              {canWriteQuest && (
                <Button
                  variant="contained"
                  onClick={() => navigate('/questionnaire/extract-questions')}
                  sx={{ ...buttonBaseStyles, ...primaryButtonStyles }}
                >
                  Upload file
                </Button>
              )}
              {canReadQuest && (
                <Button
                  variant="outlined"
                  onClick={() => navigate('/questionnaire/view-client-questions')}
                  sx={{ ...buttonBaseStyles, ...secondaryButtonStyles }}
                >
                  View client questions
                </Button>
              )}
              {!canReadQuest && !canWriteQuest && (
                <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                  You do not have permission to use questionnaire features.
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Second Card: Train Helios (admin / quest.train_helios only) */}
        {showTrainHelios && (
        <Card
          elevation={2}
          sx={{
            ...cardStyles,
            display: 'flex',
            flexDirection: 'column',
            px: '48px',
            pt: '30px',
            pb: '38px',
          }}
        >
          <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: '24px',
                mx: 'auto',
              }}
            >
              <img
                src="/Helios_admin.ico"
                alt="Admin Icon"
                style={{ width: 60, height: 60, objectFit: 'contain' }}
              />
            </Box>

            <Typography
              variant="h3"
              sx={{
                fontWeight: typography.fontWeight.bold,
                fontSize: typography.fontSize.xl,
                color: colors.text.primary,
                mb: '12px',
                textAlign: 'center',
              }}
            >
              Train OneSec
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: colors.text.secondary,
                fontSize: typography.fontSize.md,
                mb: '24px',
                textAlign: 'center',
              }}
            >
              Build the OneSec knowledge base by uploading documents, spreadsheets and other files for AI analysis.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '12px',
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="contained"
                onClick={() => navigate('/questionnaire/review')}
                sx={{ ...buttonBaseStyles, ...primaryButtonStyles }}
              >
                Review answers
              </Button>
              <UpdateKnowledgeBaseButton
                variant="outlined"
                sx={{ ...buttonBaseStyles, ...secondaryButtonStyles }}
              />
            </Box>
          </CardContent>
        </Card>
        )}
      </Box>
    </PageLayout>
  );
};

export default QuestionnaireHomePage;
